import { ChangeEvent, use, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PickersInputComponentLocaleText } from '@mui/x-date-pickers';
import { Moment } from 'moment';
import { validateTimeFrames } from 'shared/utils/timeframe-utils';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AcousticTimeFrameConfiguration from 'features/residents/models/configurations/acoustic-time-frame-configuration';
import TimeFrame from 'features/residents/models/configurations/time-frame';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  acousticConfiguration: AcousticConfiguration;
  activeAllDay: boolean;
  soundThreshold: number;
  duration: number;
  valuesChanged: (newTimeFrameConfigurations: AcousticTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  index: number;
};

export const useAcousticTimeFrames = ({ acousticConfiguration, activeAllDay, soundThreshold, duration, valuesChanged, index }: Props) => {
  const { t } = useTranslation('residentsSettings');

  const [validationErrors, setValidationErrors] = useState<boolean[]>([]);
  const [expandTimeFrames, setExpandTimeFrames] = useState<boolean[]>(
    acousticConfiguration.acousticTimeFrameConfigurations?.map(() => false) ?? [],
  );

  const {
    detailEditState: { viewingMode, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const { formState } = useFormContext();

  const defaultTimeFrame: TimeFrame = {
    from: '00:00:00.000',
    to: '00:00:00.000',
  };
  const fullDayTimeFrame: TimeFrame = {
    from: '00:00:00.000',
    to: '23:59:59.999',
  };

  const addTimeFrameConfiguration = () => {
    const newTimeFrameConfigurations = [...(acousticConfiguration.acousticTimeFrameConfigurations ?? [])];
    newTimeFrameConfigurations.push({
      duration: 4,
      soundThreshold: 4,
      timeFrame: defaultTimeFrame,
    });

    valuesChanged(newTimeFrameConfigurations, activeAllDay);

    setExpandTimeFrames((prev) => [...prev, true]);
  };

  const removeTimeFrameConfiguration = (frameIndex: number) => {
    const newTimeFrameConfigurations = [...(acousticConfiguration.acousticTimeFrameConfigurations ?? [])];
    newTimeFrameConfigurations.splice(frameIndex, 1);

    valuesChanged(newTimeFrameConfigurations, activeAllDay);

    setExpandTimeFrames((prev) => {
      const newExpandTimeFrames = [...prev];
      newExpandTimeFrames.splice(frameIndex, 1);
      return newExpandTimeFrames;
    });
  };

  const onTimeFrameChanged = (newValue: Moment | null, frameIndex: number, isFromField: boolean) => {
    const newTimeFrameConfigurations = [...(acousticConfiguration.acousticTimeFrameConfigurations ?? [])];

    const formattedValue = newValue?.format('HH:mm:ss') ?? '';
    if (isFromField) {
      newTimeFrameConfigurations[frameIndex].timeFrame.from = formattedValue;
    } else {
      newTimeFrameConfigurations[frameIndex].timeFrame.to = formattedValue;
    }

    valuesChanged(newTimeFrameConfigurations, activeAllDay);
  };

  const validate = (newTimeFrameConfigurations: AcousticTimeFrameConfiguration[]) => {
    const newValidationErrors = validateTimeFrames(newTimeFrameConfigurations.map((x) => x.timeFrame));

    setValidationErrors(newValidationErrors);

    if (newValidationErrors.every((x) => !x)) {
      residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    } else {
      residentSettingEditForm.setError(`organisationUnitScenarios.${index}`, { type: 'pattern' });
    }
  };

  const setAllDayTimeFrame = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const allDayTimeFrame = {
        duration: duration,
        soundThreshold: soundThreshold,
        timeFrame: fullDayTimeFrame,
      };
      valuesChanged([allDayTimeFrame], checked);
    } else {
      const newTimeFrameConfigurations = [...(acousticConfiguration.acousticTimeFrameConfigurations ?? [])];
      if (newTimeFrameConfigurations.length === 0) {
        newTimeFrameConfigurations.push({
          duration: 4,
          soundThreshold: 4,
          timeFrame: defaultTimeFrame,
        });
      } else if (
        newTimeFrameConfigurations.length === 1 &&
        newTimeFrameConfigurations[0].timeFrame.from === fullDayTimeFrame.from &&
        newTimeFrameConfigurations[0].timeFrame.to === fullDayTimeFrame.to
      ) {
        newTimeFrameConfigurations[0] = {
          ...newTimeFrameConfigurations[0],
          timeFrame: defaultTimeFrame,
          duration: 4,
          soundThreshold: 4,
        };
      }
      valuesChanged(newTimeFrameConfigurations, checked);

      setExpandTimeFrames(newTimeFrameConfigurations.map(() => true));
    }
  };

  const timePickerLocaleText: PickersInputComponentLocaleText = {
    fieldHoursPlaceholder: () => t('timePickerLocale.fieldHoursPlaceholder'),
    fieldMinutesPlaceholder: () => t('timePickerLocale.fieldMinutesPlaceholder'),
  };

  const toggleExpandTimeFrames = (index: number) => {
    setExpandTimeFrames((prev) => {
      const newExpandTimeFrames = [...prev];
      newExpandTimeFrames[index] = !newExpandTimeFrames[index];
      return newExpandTimeFrames;
    });
  };

  useEffect(() => {
    if (formState.isSubmitting && acousticConfiguration.acousticTimeFrameConfigurations != null) {
      validate(acousticConfiguration.acousticTimeFrameConfigurations);
    }
  }, [formState.isSubmitting, acousticConfiguration.acousticTimeFrameConfigurations]);

  useEffect(() => {
    setValidationErrors([]);
  }, [viewingMode]);

  return {
    viewingMode,
    validationErrors,
    timePickerLocaleText,
    addTimeFrameConfiguration,
    removeTimeFrameConfiguration,
    onTimeFrameChanged,
    setAllDayTimeFrame,
    expandTimeFrames,
    toggleExpandTimeFrames,
  };
};
